<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <FancyServerTable
      v-if="isStateLoaded"
      ref="fancyTableRef"
      :data="suppliersTableData"
      :columns="suppliersTableHeaders"
      :enable-filters="true"
      :enable-pagination="true"
      :enable-column-visibility="true"
      :enable-column-pinning="true"
      :initial-column-pinning="
        currentSavedState?.columnPinning || {
          left: ['name'],
          right: ['actions'],
        }
      "
      :initial-column-visibility="currentSavedState?.columnVisibility || {}"
      :initial-column-order="currentSavedState?.columnOrder || []"
      :initial-column-sizing="currentSavedState?.columnSizing || {}"
      :initial-column-filters="currentSavedState?.columnFilters || []"
      :initial-sorting="currentSavedState?.sorting ?? []"
      :total-rows="totalRows"
      :page-index="pageIndex"
      :page-size="pageSize"
      :auto-filter="true"
      enable-row-selection
      :get-row-id="row => String(row.id)"
      @update:row-selection="handleRowSelectionChange"
      :loading="isLoading"
      @update:state="debouncedHandleTableStateChange"
      @fetch-data="fetchSuppliers"
    >
      <template #actions>
        <SuppliersTableBulkActions
          :selectedSuppliers="selectedSuppliers"
          :customerId="customerId"
          @reloadTable="getSuppliersTableList"
          @clearSelection="() => fancyTableRef?.resetRowSelection?.()"
        />
      </template>
      <template #config-manager>
        <FancyTableConfigManager
          :saved-configs="savedTableConfigs"
          :current-config="currentSavedState"
          :is-loading="false"
          :can-save-state="canSaveState"
          @save-config="handleSaveConfig"
          @update-config="handleUpdateConfig"
          @apply-config="handleApplyConfig"
          @delete-config="handleDeleteConfig"
          @mark-as-default="handleMarkAsDefault"
        />
      </template>
    </FancyServerTable>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, h } from 'vue';
import type { SupplierInList } from '@/_helpers/interfaces/suppliers/supplier-in-list.interface';
import { useI18n } from 'vue-i18n';
import { useSupplierStore } from '@/stores/supplier.store';
import {
  FancyServerTable,
  type FancyTableColumnDef,
  type ServerTableOptions,
  useTableRenderers,
  FieldType,
  FilterType,
  FancyTableConfigManager,
} from '@/components/shared/fancyTables';

import SuppliersTableRowActions from './SuppliersTableRowActions.vue';
import SuppliersTableBulkActions from './SuppliersTableBulkActions.vue';
import { useFancyTableConfig } from '@/_helpers/composables/fancy-table-config.composable';

const fancyTableRef = ref<any>(null);
const selectedSuppliers = ref<SupplierInList[]>([]);
const { GetFieldRenderer } = useTableRenderers();

const { getSuppliersList } = useSupplierStore();
const { t } = useI18n();

const props = defineProps<{
  customerId: number;
}>();

// Map filters to API params
const mapFiltersToApiParams = (filters: any[]) => {
  const params: any = {};

  filters.forEach(filter => {
    const { id, value } = filter;

    switch (id) {
      case 'supplier_number':
        if (value) params.supplier_number = value;
        break;
      case 'name':
        if (value) params.name = value;
        break;
      case 'email':
        if (value) params.email = value;
        break;
      case 'phone_number':
        if (value) params.phone_number = value;
        break;
      case 'country':
        if (value) params.country = value;
        break;
      case 'city':
        if (value) params.city = value;
        break;
      case 'zip_code':
        if (value) params.zip_code = value;
        break;
      case 'created_on':
        if (value?.from) params.created_after = value.from;
        if (value?.to) params.created_before = value.to;
        break;
      default:
        break;
    }
  });

  return params;
};

// Map sorting to API param
const mapSortingToApiParam = (sorting: any[]): string => {
  if (sorting.length === 0) return '';

  const sort = sorting[0];
  const { id, desc } = sort;
  const backendField = id; // All fields match directly

  return desc ? `-${backendField}` : backendField;
};

// Server-side fetch function
const fetchSuppliers = async (options?: ServerTableOptions) => {
  isLoading.value = true;

  try {
    const currentPageIndex = options?.pageIndex ?? 0;
    const currentPageSize = options?.pageSize ?? 10;
    const filters = options?.filters ?? [];
    const sorting = options?.sorting ?? [];

    // Update pagination state
    pageIndex.value = currentPageIndex;
    pageSize.value = currentPageSize;

    // Map filters to API params
    const filterParams = mapFiltersToApiParams(filters);

    // Map sorting to API param
    const orderingParam = mapSortingToApiParam(sorting);

    const apiParams = {
      page: currentPageIndex + 1,
      page_size: currentPageSize,
      customer: props.customerId,
      ...filterParams,
      ...(orderingParam && { ordering: orderingParam }),
    };

    const response = await getSuppliersList(apiParams);

    suppliersTableData.value = response.results;
    totalRows.value = response.count;
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    suppliersTableData.value = [];
    totalRows.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const getSuppliersTableList = async () => {
  // This is now a wrapper for fetchSuppliers for backward compatibility
  await fetchSuppliers();
};

// Initialize the table config composable
const {
  savedTableConfigs,
  currentSavedState,
  canSaveState,
  debouncedHandleTableStateChange,
  handleSaveConfig,
  handleApplyConfig,
  handleUpdateConfig,
  handleDeleteConfig,
  handleMarkAsDefault,
  isStateLoaded,
} = useFancyTableConfig({
  tableName: 'suppliers',
  fancyTableRef,
  tableLoader: getSuppliersTableList,
});

const isLoading = ref(false);
const suppliersTableData = ref<SupplierInList[]>([]);
const totalRows = ref(0);
const pageIndex = ref(0);
const pageSize = ref(10);

const suppliersTableHeaders: FancyTableColumnDef<SupplierInList>[] = [
  {
    header: t('suppliers.titles.supplier_number'),
    accessorKey: 'supplier_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 150,
  },
  {
    header: t('supplier.titles.name'),
    accessorKey: 'name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 250,
  },
  {
    header: t('suppliers.titles.email'),
    accessorKey: 'email',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 200,
  },
  {
    header: t('suppliers.titles.phone'),
    accessorKey: 'phone_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 150,
  },
  {
    header: t('suppliers.titles.country'),
    accessorKey: 'country',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 120,
  },
  {
    header: t('suppliers.titles.city'),
    accessorKey: 'city',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 120,
  },
  {
    header: t('suppliers.titles.zip_code'),
    accessorKey: 'zip_code',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 120,
  },
  {
    header: t('suppliers.titles.created_at'),
    enableSorting: true,
    accessorKey: 'created_on',
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 150,
  },
  {
    header: t('common.actions.actions'),
    accessorKey: 'actions',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
    hideFromColumnConfig: true,
    cell: info => {
      return h(SuppliersTableRowActions, {
        supplier: info.row.original,
        onReloadTable: getSuppliersTableList,
      });
    },
    size: 120,
  },
];

const handleRowSelectionChange = (selection: any[]) => {
  selectedSuppliers.value = selection;
};

onMounted(async () => {
  await getSuppliersTableList();
});
</script>
